import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
} from '@mui/material';
import { Editor, type Range } from '@tiptap/core';
import React, { useState } from 'react';
import { AiPromptTemplate } from 'src/@types/ai.js';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { UseAiPrompt } from '../AiPrompt/useAiPrompt';
import { BubbleMenuItem } from '../EditorBubbleMenu/types';
import { AiPromptPopOver } from './AiPromptPopOver';
type Item = Omit<BubbleMenuItem, 'isActive' | 'items'> & {
  items?: Item[];
};

export const AiPromptActions = ({
  prompt,
  deleteRange,
  editor,
  onClose,
}: {
  prompt: UseAiPrompt;
  onClose: () => void;
  editor: Editor;
  deleteRange?: Range;
}) => {
  const [messageSelection, setMessageSelection] = useState<'all' | 'last'>(
    'last',
  );
  const { messages, getResponse } = prompt;
  const { selectedContext } = prompt.control();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuItems, setSubmenuItems] = useState<Item[]>([]);
  const { translate } = useLocales();

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSubmenuItems([]);
  };

  const translateText = (lang: string) => {
    getResponse(
      AiPromptTemplate.TRANSLATE,
      `${translate('shared.ai_prompt.translate_to')} ${lang}`,
    );
    handlePopoverClose();
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    items: Item[] | undefined,
  ) => {
    if (anchorEl && !items) {
      setAnchorEl(null);
      setSubmenuItems([]);
    }

    if (items) {
      setAnchorEl(event.currentTarget);
      setSubmenuItems(items);
    }
    event.stopPropagation();
  };

  const startCommand = () => {
    const chain = editor.chain().focus();

    if (deleteRange) {
      chain.deleteRange(deleteRange);
    }

    return chain;
  };

  const startItems: Item[] = [
    {
      name: translate('shared.ai_prompt.improve'),
      icon: 'tabler:wand',
      command: () => {
        getResponse(
          AiPromptTemplate.IMPROVE_WRITING,
          translate('shared.ai_prompt.improve_writing'),
        );
      },
    },
    {
      name: translate('shared.ai_prompt.check_grammar'),
      icon: 'tabler:check',
      command: () => {
        getResponse(
          AiPromptTemplate.IMPROVE_GRAMMAR,
          translate('shared.ai_prompt.correct_grammar_spelling'),
        );
      },
    },
    {
      name: translate('shared.ai_prompt.translate'),
      icon: 'tabler:language-hiragana',
      command: () => {},
      items: [
        {
          name: translate('shared.languages.english'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.english'));
          },
        },
        {
          name: translate('shared.languages.spanish'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.spanish'));
          },
        },
        {
          name: translate('shared.languages.french'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.french'));
          },
        },
        {
          name: translate('shared.languages.italian'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.italian'));
          },
        },
        {
          name: translate('shared.languages.german'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.german'));
          },
        },
        {
          name: translate('shared.languages.portuguese'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.portuguese'));
          },
        },
        {
          name: translate('shared.languages.korean'),
          icon: 'tabler:language-hiragana',
          command: () => {
            translateText(translate('shared.languages.korean'));
          },
        },
      ],
    },

    {
      name: translate('shared.ai_prompt.summarize'),
      icon: 'tabler:file-text',
      command: () => {
        getResponse(
          AiPromptTemplate.SUMMARIZE,
          translate('shared.ai_prompt.summarize_text'),
        );
      },
    },
  ];

  const getAllAiMessagesContent = () => {
    return messages
      .filter((_: unknown, index: number) => index % 2 !== 0)
      .map((message: { content: string }) => message.content)
      .join('\n\n');
  };

  const promptItems: Item[] = [
    {
      name:
        messageSelection === 'all'
          ? translate('shared.ai_prompt.all_messages')
          : translate('shared.ai_prompt.last_message'),
      icon: messageSelection === 'all' ? 'tabler:list' : 'tabler:message-dots',
      command: () => {},
      items: [
        {
          name: translate('shared.ai_prompt.all_messages'),
          icon: 'tabler:list',
          command: () => {
            setMessageSelection('all');
            handlePopoverClose();
          },
        },
        {
          name: translate('shared.ai_prompt.last_message'),
          icon: 'tabler:message-dots',
          command: () => {
            setMessageSelection('last');
            handlePopoverClose();
          },
        },
      ],
    },
    {
      name: translate('shared.ai_prompt.insert_below'),
      icon: 'ci:add-row',
      command: () => {
        const activeResponse =
          messageSelection === 'all'
            ? getAllAiMessagesContent()
            : messages[messages.length - 1]?.content || '';

        if (!activeResponse) return;

        startCommand()
          .createParagraphNear()
          .insertMarkdown(activeResponse)
          .run();
        onClose();
      },
    },
    // {
    //   name: translate('shared.ai_prompt.make_it_shorter'),
    //   icon: 'mdi:text-short',
    //   command: () => {
    //     prompt.followUp('Make it shorter');
    //   },
    // },
    // {
    //   name: translate('shared.ai_prompt.make_it_longer'),
    //   icon: 'mdi:text-long',
    //   command: () => {
    //     prompt.followUp('Make it longer');
    //   },
    // },
    {
      name: translate('shared.ai_prompt.replace_selection'),
      icon: 'tabler:check',
      command: () => {
        const activeResponse =
          messageSelection === 'all'
            ? getAllAiMessagesContent()
            : messages[messages.length - 1]?.content || '';

        if (!activeResponse) return;

        startCommand()
          .setTextSelection(prompt.selectionRef.current)
          .insertMarkdown(activeResponse, {
            inlineSingleParagraph: true,
          })
          .run();
        onClose();
      },
    },
    {
      name: translate('shared.ai_prompt.dismiss'),
      icon: 'tabler:x',
      command: () => {
        onClose();
      },
    },
  ];

  const hasMessages = messages.length > 0;
  const items = hasMessages ? promptItems : selectedContext ? startItems : [];

  if (!items.length) return null;

  return (
    <Paper
      elevation={1}
      sx={{
        marginBottom: 2,
        width: '100%',
      }}
    >
      <MenuList
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          p: 1,
          alignItems: 'center',

          justifyContent: 'space-evenly',
        }}
      >
        {items.map((item, index) => (
          <MenuItem
            key={item.name}
            onClick={(event) => {
              item.items
                ? handleMenuItemClick(event, item.items)
                : item.command();
            }}
            sx={{
              marginBottom: '0 !important',
            }}
            onMouseLeave={() => !item.items?.length && handlePopoverClose()}
          >
            <>
              <ListItemIcon>
                <Iconify icon={item.icon} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                {item.name}
              </ListItemText>
              {item.items && <Iconify icon={'tabler:chevron-down'} ml={2} />}
            </>
          </MenuItem>
        ))}
      </MenuList>
      <AiPromptPopOver
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        submenuItems={submenuItems as any}
      />
    </Paper>
  );
};
